import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from 'Components/Button'
import { Table, TableRow, TableHeader } from 'Components/Table'
import { useHistory } from 'react-router-dom'
import BottomButton from 'Components/BottomButton'
import Coins from 'Assets/Icons/doodle_coins.png'
import { useAdapter } from 'Stores/Adapter/useAdapter'
import { Config } from 'Config'
import { ContractMethod } from 'Adapters/Contract'
import { useTransactions } from 'Stores/Transactions/useTransactions'
import { useToasts } from 'react-toast-notifications'
import { getPrecision } from 'Utils/Decimals'
import { Trans, useTranslation } from 'react-i18next'
import { usePools } from '../../Stores/Pools/usePools'
import { BN } from '../../Utils/BigNumber'
import { useBalances } from '../../Stores/Balances/useBalances'

import './ClaimRewards.scss'

const ClaimRewards: React.FC = () => {
  const history = useHistory()
  const { addToast } = useToasts()
  const { execute, adapter } = useAdapter()
  const [claimsDetail, setClaimsDetail] = useState<
    { contract_address: string; amount: string; name: string }[]
  >([])
  const { addExpectedTransaction } = useTransactions()
  const { t } = useTranslation()
  const { pairs } = usePools()
  const { getBalanceByToken } = useBalances()

  const liquidityPairs = useMemo(() => {
    const reducedPairs = pairs.map((p) => ({
      balance: getBalanceByToken(p.contract_address),
      ...p
    }))
    return reducedPairs.filter((p) => BN(p.balance).isGreaterThan(0))
  }, [getBalanceByToken, pairs])

  const updateClaimable = useCallback(async () => {
    if (adapter) {
      for (const contract of liquidityPairs) {
        const pendingFee = await execute(
          contract.contract_address,
          ContractMethod.PENDING_FEE_EARN
        )

        const amount = BN(pendingFee.value)
          .dividedBy(getPrecision(Config.contracts.UP.address))
          .toFixed()

        const uPair = {
          ...contract,
          amount
        }

        if (BN(amount).isGreaterThan(0)) {
          setClaimsDetail((p) => [...p, uPair])
        }
      }
    }
  }, [execute, adapter, liquidityPairs])

  useEffect(() => {
    updateClaimable()
    // eslint-disable-next-line
  }, [adapter])

  const onClaimClick = useCallback(
    async (detail: typeof claimsDetail[number]) => {
      if (BN(detail.amount).isZero()) {
        return addToast(<div>{t('claim-rewards.claiming-error')}</div>, {
          appearance: 'info'
        })
      }

      if (adapter) {
        const claimResult = await execute(
          detail.contract_address,
          ContractMethod.CLAIM_FEE,
          {},
          true
        )

        addExpectedTransaction({
          transaction: claimResult,
          movements: {
            received: {
              name: 'UP',
              value: detail.amount
            }
          }
        })

        if (claimResult.success) {
          history.push(`/liquidity/claim-rewards/success/${claimResult.hash}`)
        } else {
          history.push('/failed')
        }
      }
    },
    [
      t,
      execute,
      addExpectedTransaction,
      addToast,
      adapter,
      history,
      claimsDetail
    ]
  )

  return (
    <div className="ClaimRewards">
      <div className="ClaimRewards__header">
        <img src={Coins} alt="Claim Rewards" />
        <span>
          <h1>{t('claim-rewards.title')}</h1>
          <p>{t('claim-rewards.p-1')}</p>
          <p>
            <Trans i18nKey="for-more-info-press-here">
              For more information press{' '}
              <a
                href="https://unifiprotocol.zendesk.com/hc/en-us"
                title="more information"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              .
            </Trans>
          </p>
        </span>
      </div>

      <div className="ClaimRewards__summary">
        <Table>
          <TableHeader
            columns={['Tokens', 'Estimated Claimable Balance', '']}
          />
          {claimsDetail.map((detail, i) => {
            return (
              <TableRow
                key={i}
                columns={[
                  detail.name,
                  `${detail.amount} UP`,
                  <div className="text-align-right">
                    <Button onClick={() => onClaimClick(detail)}>Claim</Button>
                  </div>
                ]}
              />
            )
          })}
        </Table>
      </div>

      <div className="Wrapper-Bottom-Button">
        <BottomButton label="Back" onClick={() => history.push('/liquidity')} />
      </div>
    </div>
  )
}

export { ClaimRewards }
